import type {
  CornerstoneImageLike,
  MetadataItem,
  SliceMetadata,
} from "./imagingFileViewer.types";

/**
 * Per-slice DICOM detail, read from the slice the viewer already decoded rather than from the API:
 * cornerstone-wado-image-loader exposes the dicom-parser dataset on `image.data`, so the tags are
 * in the browser the moment the pixels are — no second request, no core-model column, and the
 * panel always describes the frame on screen.
 *
 * Study- and series-level attributes are constant across every slice and belong in the Imaging
 * Studies table instead.
 */

const TAG = {
  instanceNumber: "x00200013",
  sliceLocation: "x00201041",
  sliceThickness: "x00180050",
  spacingBetweenSlices: "x00180088",
  imagePositionPatient: "x00200032",
  acquisitionTime: "x00080032",
  contentTime: "x00080033",
  exposure: "x00181152",
  exposureTime: "x00181150",
  windowCenter: "x00281050",
  windowWidth: "x00281051",
  rows: "x00280010",
  columns: "x00280011",
  pixelSpacing: "x00280030",
  bitsStored: "x00280101",
  photometricInterpretation: "x00280004",
  rescaleSlope: "x00281053",
  rescaleIntercept: "x00281052",
} as const;

export const UNKNOWN = "-";

function trimNumber(value: number, places = 3): string {
  if (!Number.isFinite(value)) return UNKNOWN;
  return String(Number(value.toFixed(places)));
}

/** Cornerstone gives a single value, or one per window when the file defines several. */
function firstOf(value: number | number[] | undefined): number | undefined {
  if (Array.isArray(value)) return value[0];
  return value;
}

/** DICOM multi-values are backslash separated: "134.5\\-12.25\\8". */
function formatMultiValue(raw: string | undefined, unit = ""): string {
  if (!raw) return UNKNOWN;
  const parts = raw
    .split("\\")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return UNKNOWN;
  const formatted = parts
    .map((part) =>
      Number.isFinite(Number(part)) ? trimNumber(Number(part)) : part,
    )
    .join(", ");
  return unit ? `${formatted} ${unit}` : formatted;
}

/** DICOM TM is "HHMMSS.frac". */
function formatDicomTime(raw: string | undefined): string {
  if (!raw) return UNKNOWN;
  const digits = raw.trim().split(".")[0];
  if (digits.length < 6) return raw.trim();
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}`;
}

function wholeNumber(value: number | undefined): string {
  return value == null || !Number.isFinite(value) ? UNKNOWN : String(value);
}

function numberWithUnit(value: number | undefined, unit: string): string {
  return value == null || !Number.isFinite(value)
    ? UNKNOWN
    : `${trimNumber(value)} ${unit}`;
}

function present(items: MetadataItem[]): MetadataItem[] {
  return items.filter((item) => item.value !== UNKNOWN && item.value !== "");
}

export function extractSliceMetadata(
  image: CornerstoneImageLike | null,
): SliceMetadata {
  if (!image) return { slice: [], image: [] };
  const data = image.data;

  const rows = image.rows ?? data?.uint16?.(TAG.rows);
  const columns = image.columns ?? data?.uint16?.(TAG.columns);
  const rowSpacing = image.rowPixelSpacing;
  const columnSpacing = image.columnPixelSpacing;
  const pixelSpacing =
    rowSpacing != null && columnSpacing != null
      ? `${trimNumber(columnSpacing)} x ${trimNumber(rowSpacing)} mm`
      : formatMultiValue(data?.string(TAG.pixelSpacing), "mm");

  // Prefer cornerstone's values where it has them: it has already resolved defaults and picked
  // between multi-valued windows.
  const windowCenter =
    firstOf(image.windowCenter) ?? data?.floatString(TAG.windowCenter);
  const windowWidth =
    firstOf(image.windowWidth) ?? data?.floatString(TAG.windowWidth);

  const slice = present([
    {
      label: "Instance number",
      value: wholeNumber(data?.intString(TAG.instanceNumber)),
    },
    {
      label: "Slice location",
      value: numberWithUnit(data?.floatString(TAG.sliceLocation), "mm"),
    },
    {
      label: "Slice thickness",
      value: numberWithUnit(data?.floatString(TAG.sliceThickness), "mm"),
    },
    {
      label: "Spacing between slices",
      value: numberWithUnit(data?.floatString(TAG.spacingBetweenSlices), "mm"),
    },
    {
      label: "Position (x, y, z)",
      value: formatMultiValue(data?.string(TAG.imagePositionPatient), "mm"),
    },
    {
      label: "Acquired",
      value: formatDicomTime(
        data?.string(TAG.acquisitionTime) ?? data?.string(TAG.contentTime),
      ),
    },
    {
      label: "Exposure",
      value: numberWithUnit(data?.intString(TAG.exposure), "mAs"),
    },
    {
      label: "Exposure time",
      value: numberWithUnit(data?.intString(TAG.exposureTime), "ms"),
    },
  ]);

  const imageItems = present([
    {
      label: "Matrix",
      value: rows != null && columns != null ? `${columns} x ${rows}` : UNKNOWN,
    },
    { label: "Pixel spacing", value: pixelSpacing },
    {
      label: "Window C / W",
      value:
        windowCenter != null && windowWidth != null
          ? `${trimNumber(windowCenter)} / ${trimNumber(windowWidth)}`
          : UNKNOWN,
    },
    {
      label: "Photometric",
      value: data?.string(TAG.photometricInterpretation)?.trim() ?? UNKNOWN,
    },
    {
      label: "Bits stored",
      value: wholeNumber(data?.uint16?.(TAG.bitsStored)),
    },
    {
      label: "Rescale (slope / intercept)",
      value:
        image.slope != null && image.intercept != null
          ? `${trimNumber(image.slope)} / ${trimNumber(image.intercept)}`
          : formatMultiValue(
              [
                data?.string(TAG.rescaleSlope),
                data?.string(TAG.rescaleIntercept),
              ]
                .filter(Boolean)
                .join("\\"),
            ),
    },
  ]);

  return { slice, image: imageItems };
}
